import {
    LIVE_AUCTION_PAUSED,
    LIVE_BID_ACCEPTED,
    LIVE_BID_RETRACTED,
    LIVE_LOT_PASSED,
    LIVE_LOT_REOPENED,
    LIVE_LOT_SKIPPED,
    LIVE_LOT_SOLD,
    LIVE_LOT_UNSOLD,
    LIVE_MISSIVE,
    LIVE_NEXT_LOT_LOADED,
    LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS,
} from './actions';
import { acceptedMessageBuilder } from '@/utils/acceptedMessageBuilder';
import { createSelector } from 'reselect';
import { GlobalState } from '../rootReducer';

/* helpers */
const addMessage = (messages, newMessages) => {
    messages = [...messages];
    if (Array.isArray(newMessages)) {
        newMessages.forEach((message) => doAdMessage(messages, message));
    } else {
        doAdMessage(messages, newMessages);
    }

    if (messages.length > 500) {
        messages = messages.splice(messages.length - 50, 50);
    }
    return messages;
};

const doAdMessage = (messages, message) => {
    messages.push(message);
    messages.sort((a, b) => a.created - b.created);
};

/* reducer */
const DEFAULT_STATE = {
    byId: {},
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    const payload = action.payload || {};
    const {
        amount,
        approved,
        ask,
        askPrice,
        bidderId,
        bidderOutbid,
        catalogId,
        created,
        currency,
        limit,
        lotNumber,
        missiveText,
        myBid,
        paddleNumber,
        previousBidAmount,
        source,
        wasApproved,
    } = payload;

    let existing = state.byId[catalogId] || [];

    switch (action.type) {
        case LIVE_AUCTION_PAUSED:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, {
                        created,
                        data: {},
                        type: 'paused',
                    }),
                },
            };
        case LIVE_BID_ACCEPTED:
            const newMessages = acceptedMessageBuilder({
                amount,
                bidderId,
                created,
                currency,
                myBid,
                outbid: bidderOutbid,
                paddleNumber,
                previousBidAmount,
                source,
            });
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, newMessages),
                },
            };
        case LIVE_BID_RETRACTED:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, {
                        created,
                        data: {
                            amount: ask,
                            currency,
                            myBid,
                        },
                        type: 'retracted',
                    }),
                },
            };
        case LIVE_LOT_PASSED:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, {
                        created,
                        data: { lotNumber },
                        type: 'passed',
                    }),
                },
            };
        case LIVE_LOT_REOPENED:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, {
                        created,
                        data: { lotNumber },
                        type: 'reopened',
                    }),
                },
            };
        case LIVE_LOT_SKIPPED:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, {
                        created,
                        data: { lotNumber },
                        type: 'skipped',
                    }),
                },
            };
        case LIVE_LOT_SOLD:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, {
                        created,
                        data: {
                            amount,
                            bidderId,
                            currency,
                            lotNumber,
                            myBid,
                            paddleNumber,
                        },
                        type: 'sold',
                    }),
                },
            };
        case LIVE_LOT_UNSOLD:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, {
                        created,
                        data: { lotNumber },
                        type: 'unsold',
                    }),
                },
            };
        case LIVE_MISSIVE:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, {
                        created,
                        text: missiveText,
                        type: 'missive',
                    }),
                },
            };
        case LOAD_LIVE_CATALOG_REGISTRATION_SUCCESS:
            if (!wasApproved && approved) {
                existing = addMessage(existing, {
                    created,
                    data: { limit },
                    type: 'approved',
                });
            }

            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: existing,
                },
            };
        case LOAD_LIVE_CATALOG_STATUS_SUCCESS:
            if (action.payload.status === 'paused' && existing.length === 0) {
                existing = addMessage(existing, {
                    created,
                    data: {},
                    type: 'paused',
                });
            }

            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: existing,
                },
            };
        case LIVE_NEXT_LOT_LOADED:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [catalogId]: addMessage(existing, {
                        created,
                        data: {
                            amount: askPrice,
                            currency,
                            lotNumber,
                        },
                        type: 'opened',
                    }),
                },
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.consoleMessages;
const idSelector = (state: GlobalState, id) => id;
const smallConsoleSelector = (state: GlobalState, id, smallConsole) => smallConsole;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

export const getMessages: (arg0: any, arg1: number, arg2: boolean) => any = createSelector(
    [byIdSelector, idSelector, smallConsoleSelector],
    (byId, id, smallConsole) => {
        const messages = byId[id] || [];

        if (smallConsole) {
            return messages.filter(
                (x) =>
                    x.type === 'accepted' ||
                    x.type === 'ended' ||
                    x.type === 'opened' ||
                    x.type === 'passed' ||
                    x.type === 'paused' ||
                    x.type === 'skipped' ||
                    x.type === 'sold' ||
                    x.type === 'unsold'
            );
        }
        return messages;
    }
);
